/**
 * ChecklistStatsDashboard Component
 * 
 * Dashboard per visualizzare statistiche e metriche delle checklist
 * Usa i servizi API per ottenere dati aggregati
 */

import React, { useState, useEffect } from 'react';
import {
  Paper,
  Title,
  Grid,
  Group,
  Text,
  RingProgress,
  Stack,
  Badge,
  Card,
  SimpleGrid,
  ThemeIcon,
  Progress,
  Alert,
  LoadingOverlay,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconChecklist,
  IconCheck,
  IconClock,
  IconAlertCircle,
  IconTrendingUp,
  IconUsers,
  IconRefresh,
  IconChartBar,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import { checklistItemService } from '../../services/checklistItemService';
import { 
  ChecklistStatisticsDto, 
  ChecklistTaskStatisticsDto,
  UserTaskStatistics
} from '../../types/checklist-types';

interface StatCard {
  title: string;
  value: string | number;
  description: string;
  icon: React.ReactNode;
  color: string;
  progress?: number;
}

const ChecklistStatsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ChecklistStatisticsDto | null>(null);
  const [taskStats, setTaskStats] = useState<ChecklistTaskStatisticsDto[]>([]);
  const [userStats, setUserStats] = useState<UserTaskStatistics[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Carica tutte le statistiche
  const loadStatistics = async () => {
    try {
      setLoading(true);

      // Carica statistiche generali
      const generalStats = await checklistItemService.getStatistics();
      setStats(generalStats);

      // Ottieni statistiche task e utenti dalle statistiche generali
      if (generalStats.mostActiveTasks) {
        setTaskStats(generalStats.mostActiveTasks.slice(0, 10).map(task => ({
          taskId: task.taskId,
          taskTitle: task.taskTitle,
          totalItems: task.totalChecklistItems,
          completedItems: task.completedItems,
          pendingItems: task.totalChecklistItems - task.completedItems,
          overdueItems: 0,
          completionPercentage: task.completionPercentage,
        })));
      }

      if (generalStats.topUsers) {
        setUserStats(generalStats.topUsers.slice(0, 10));
      }

      setLastUpdated(new Date());

      notifications.show({
        title: 'Statistiche aggiornate',
        message: 'Dati caricati con successo',
        color: 'green'
      });

    } catch (error) {
      console.error('Errore nel caricamento delle statistiche:', error);
      notifications.show({
        title: 'Errore',
        message: 'Errore nel caricamento delle statistiche',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, []);

  // Prepara le card delle statistiche principali
  const getStatCards = (): StatCard[] => {
    if (!stats) return [];

    const completionRate = stats.totalItems > 0 ? 
      Math.round((stats.completedItems / stats.totalItems) * 100) : 0;

    return [
      {
        title: 'Totale Items',
        value: stats.totalItems,
        description: 'Items totali nelle checklist',
        icon: <IconChecklist size={24} />,
        color: 'blue',
      },
      {
        title: 'Items Completati',
        value: stats.completedItems,
        description: `${completionRate}% del totale`,
        icon: <IconCheck size={24} />,
        color: 'green',
        progress: completionRate
      },
      {
        title: 'Items Pendenti',
        value: stats.pendingItems,
        description: 'Da completare',
        icon: <IconClock size={24} />,
        color: 'orange',
      },
      {
        title: 'Tasks con Checklist',
        value: stats.tasksWithChecklists,
        description: 'Con checklist attive',
        icon: <IconUsers size={24} />,
        color: 'violet',
      }
    ];
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(date);
  };

  return (
    <Stack gap="lg" pos="relative">
      <LoadingOverlay visible={loading} />
      
      {/* Header */}
      <Paper p="md" withBorder>
        <Group justify="space-between">
          <div>
            <Title order={2}>
              <IconChartBar size={28} /> Dashboard Checklist
            </Title>
            <Text c="dimmed" size="sm">
              Statistiche e metriche delle checklist del sistema
            </Text>
          </div>
          <Group>
            <Text size="xs" c="dimmed">
              Ultimo aggiornamento: {formatDate(lastUpdated)}
            </Text>
            <Tooltip label="Aggiorna statistiche">
              <ActionIcon onClick={loadStatistics} variant="light">
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Paper>

      {stats ? (
        <>
          {/* Cards principali */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            {getStatCards().map((card, index) => (
              <Card key={index} shadow="sm" p="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text fw={500} size="sm">
                    {card.title}
                  </Text>
                  <ThemeIcon color={card.color} variant="light">
                    {card.icon}
                  </ThemeIcon>
                </Group>

                <Text size="xl" fw={700} mb="xs">
                  {card.value}
                </Text>

                <Text size="xs" c="dimmed" mb={card.progress ? "md" : 0}>
                  {card.description}
                </Text>

                {card.progress !== undefined && (
                  <Progress 
                    value={card.progress} 
                    color={card.color} 
                    size="sm"
                    radius="xl"
                  />
                )}
              </Card>
            ))}
          </SimpleGrid>

          {/* Statistiche dettagliate */}
          <Grid>
            {/* Top Tasks */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper p="md" withBorder h="400px">
                <Title order={3} mb="md">
                  Tasks con più Checklist Items
                </Title>
                
                {taskStats.length > 0 ? (
                  <Stack gap="sm">
                    {taskStats.slice(0, 5).map((task) => (
                      <Paper key={task.taskId} p="sm" withBorder>
                        <Group justify="space-between" mb="xs">
                          <Text fw={500} size="sm" truncate>
                            {task.taskTitle || `Task #${task.taskId}`}
                          </Text>
                          <Badge size="sm" variant="light">
                            {task.totalItems} items
                          </Badge>
                        </Group>
                        
                        <Group justify="space-between" align="center">
                          <Text size="xs" c="dimmed">
                            {task.completedItems}/{task.totalItems} completati
                          </Text>
                          <Text size="xs" fw={500} c="green">
                            {Math.round(task.completionPercentage)}%
                          </Text>
                        </Group>
                        
                        <Progress 
                          value={task.completionPercentage} 
                          color="green"
                          size="xs"
                          mt="xs"
                        />
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert>
                    <Text size="sm">Nessun dato disponibile per i task</Text>
                  </Alert>
                )}
              </Paper>
            </Grid.Col>

            {/* Top Users */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper p="md" withBorder h="400px">
                <Title order={3} mb="md">
                  Utenti più Attivi
                </Title>
                
                {userStats.length > 0 ? (
                  <Stack gap="sm">
                    {userStats.slice(0, 5).map((user) => (
                      <Paper key={user.userId} p="sm" withBorder>
                        <Group justify="space-between" mb="xs">
                          <Text fw={500} size="sm">
                            {user.userName || `User #${user.userId}`}
                          </Text>
                          <Badge size="sm" variant="light" color="blue">
                            {user.totalAssignedItems} assegnati
                          </Badge>
                        </Group>
                        
                        <SimpleGrid cols={3}>
                          <div>
                            <Text size="xs" c="dimmed">Completati</Text>
                            <Text size="sm" fw={500} c="green">
                              {user.completedItems}
                            </Text>
                          </div>
                          <div>
                            <Text size="xs" c="dimmed">Pendenti</Text>
                            <Text size="sm" fw={500} c="orange">
                              {user.totalAssignedItems - user.completedItems}
                            </Text>
                          </div>
                          <div>
                            <Text size="xs" c="dimmed">Tasso</Text>
                            <Text size="sm" fw={500}>
                              {Math.round(user.completionRate)}%
                            </Text>
                          </div>
                        </SimpleGrid>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert>
                    <Text size="sm">Nessun dato disponibile per gli utenti</Text>
                  </Alert>
                )}
              </Paper>
            </Grid.Col>
          </Grid>

          {/* Metriche aggregate */}
          {stats.totalItems > 0 && (
            <Paper p="md" withBorder>
              <Title order={3} mb="md">
                Panoramica Generale
              </Title>
              
              <Grid>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Group justify="center">
                    <RingProgress
                      size={140}
                      thickness={12}
                      sections={[
                        { 
                          value: (stats.completedItems / stats.totalItems) * 100, 
                          color: 'green' 
                        }
                      ]}
                      label={
                        <div style={{ textAlign: 'center' }}>
                          <Text fw={700} size="lg">
                            {Math.round((stats.completedItems / stats.totalItems) * 100)}%
                          </Text>
                          <Text size="xs" c="dimmed">
                            Completamento
                          </Text>
                        </div>
                      }
                    />
                  </Group>
                </Grid.Col>
                
                <Grid.Col span={{ base: 12, md: 8 }}>
                  <SimpleGrid cols={2}>
                    <div>
                      <Text size="xs" c="dimmed" mb="xs">ITEMS COMPLETATI OGGI</Text>
                      <Group gap="xs">
                        <IconTrendingUp size={16} color="green" />
                        <Text size="lg" fw={700}>
                          {stats.itemsCompletedToday || 0}
                        </Text>
                      </Group>
                    </div>
                    
                    <div>
                      <Text size="xs" c="dimmed" mb="xs">COMPLETAMENTO TASK</Text>
                      <Group gap="xs">
                        <IconChartBar size={16} color="blue" />
                        <Text size="lg" fw={700}>
                          {Math.round(stats.taskCompletionPercentage)}%
                        </Text>
                      </Group>
                    </div>
                    
                    <div>
                      <Text size="xs" c="dimmed" mb="xs">TASKS CON CHECKLIST</Text>
                      <Group gap="xs">
                        <IconUsers size={16} color="violet" />
                        <Text size="lg" fw={700}>
                          {stats.tasksWithChecklists}
                        </Text>
                      </Group>
                    </div>
                    
                    <div>
                      <Text size="xs" c="dimmed" mb="xs">ITEMS IN SCADENZA</Text>
                      <Group gap="xs">
                        <IconAlertCircle size={16} color="orange" />
                        <Text size="lg" fw={700}>
                          {stats.overdueItems}
                        </Text>
                      </Group>
                    </div>
                  </SimpleGrid>
                </Grid.Col>
              </Grid>
            </Paper>
          )}
        </>
      ) : (
        !loading && (
          <Alert icon={<IconAlertCircle />} title="Nessun dato" color="blue">
            Non ci sono statistiche disponibili al momento. 
            Le statistiche verranno popolate quando ci saranno checklist items nel sistema.
          </Alert>
        )
      )}
    </Stack>
  );
};

export default ChecklistStatsDashboard;